import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ServerCredential } from '../api/types.js';
import { useApiClient } from './use-api-client.js';

/**
 * The credentials a server's sessions use and when each ends (ADR-033); read again every minute, as the API does, and
 * every 3 seconds while a login started from the page waits for its approval
 */
export function useCredentials(serverId: string, enabled = true) {
  const api = useApiClient();
  return useQuery({
    queryKey: ['credentials', serverId],
    queryFn: () => api.credentials(serverId),
    enabled,
    refetchInterval: (q) => (q.state.data?.logins?.some((l) => l.status === 'waiting') ? 3_000 : 60_000),
  });
}

/** Log in again: the server starts the login, the page then shows its URL and code */
export function useStartLogin(serverId: string) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ kind, name }: { kind: ServerCredential['kind']; name: string }) => api.startLogin(serverId, kind, name),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['credentials', serverId] }),
  });
}
